/**
 * Detecting and clearing an optimistic-lock conflict.
 *
 * Centralised so every caller reports a 409 the same way. PRD §6.2 requires
 * conflicts to be surfaced rather than silently overwritten, and a page that
 * folds them into a generic error message does not surface anything — the user
 * retries the same stale write.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.9, PRD §6.2
 */
import { useCallback, useState } from 'react';

import { type ApiError, ProblemType, isApiError } from './problem';

/** A detected conflict: what the caller had submitted. */
export interface ConflictState {
  submittedVersion: number | undefined;
}

export interface UseConflictResult {
  /** Non-null while a conflict is unacknowledged. */
  conflict: ConflictState | null;
  /**
   * Records a conflict if the error is one.
   *
   * @returns true when the error WAS a conflict and has been recorded, so the
   *   caller can skip its own generic error handling.
   */
  reportIfConflict: (error: unknown) => boolean;
  clear: () => void;
}

/**
 * True when the error is a version conflict.
 *
 * A type predicate rather than a boolean, so the narrowing survives into the
 * caller — otherwise reading `problem.expectedVersion` needs a second
 * `isApiError` check and the dead branch that comes with it.
 */
export function isVersionConflict(error: unknown): error is ApiError {
  return isApiError(error) && error.is(ProblemType.VERSION_CONFLICT);
}

export function useConflict(): UseConflictResult {
  const [conflict, setConflict] = useState<ConflictState | null>(null);

  const reportIfConflict = useCallback((error: unknown): boolean => {
    if (!isVersionConflict(error)) return false;

    setConflict({ submittedVersion: error.problem.expectedVersion });

    return true;
  }, []);

  const clear = useCallback(() => {
    setConflict(null);
  }, []);

  return { conflict, reportIfConflict, clear };
}
